import { useCallback, useRef } from 'react';
import { calmTokens as t } from '../../shared/tokens';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useDismissable from '../../hooks/useDismissable';

/**
 * Primitive de modale partagée.
 *
 *   overlay (fixed, 100dvh, safe-area, centré)
 *     └─ carte (flex column, max-height bornée)
 *          ├─ header  (flex-shrink: 0)          — toujours visible
 *          ├─ body    (flex: 1, overflow-y)     — seule zone qui défile
 *          └─ footer  (flex-shrink: 0)          — boutons toujours atteignables
 *
 * La `max-height` bornée de la carte garantit qu'elle ne peut jamais déborder
 * de l'overlay : le centrage reste donc valide, y compris en paysage ou
 * clavier virtuel ouvert. Les styles vivent dans `index.css`
 * (`.calm-modal-*`) pour ne pas être dupliqués quand deux modales s'empilent.
 */
export default function CalmModal({
  open,
  onClose,
  as = 'div',
  onSubmit,
  labelledBy,
  describedBy,
  zIndex = 60,
  maxWidth = 440,
  header = null,
  footer = null,
  children,
  dismissible = true,
  closeOnBackdrop = true,
  initialFocusRef = null,
  backdropClassName = '',
  cardClassName = '',
  overlayStyle = null,
  cardStyle = null,
  bodyStyle = null,
}) {
  const cardRef = useRef(null);

  useBodyScrollLock(open);
  useDismissable({
    open,
    onClose,
    containerRef: cardRef,
    initialFocusRef,
    dismissible,
  });

  const handleBackdrop = useCallback(
    (e) => {
      // `mousedown` plutôt que `click` : évite la fermeture quand on relâche
      // la souris sur l'overlay après une sélection de texte dans la carte.
      if (!closeOnBackdrop || !dismissible) return;
      if (e.target === e.currentTarget) onClose?.();
    },
    [closeOnBackdrop, dismissible, onClose]
  );

  if (!open) return null;

  const Card = as === 'form' ? 'form' : 'div';

  return (
    <div
      className={`calm-modal-overlay ${backdropClassName}`.trim()}
      style={{ zIndex, ...(overlayStyle || {}) }}
      onMouseDown={handleBackdrop}
    >
      <Card
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        tabIndex={-1}
        onSubmit={as === 'form' ? onSubmit : undefined}
        className={`calm-modal-card ${cardClassName}`.trim()}
        style={{
          maxWidth,
          background: t.surface,
          border: `1px solid ${t.borderS}`,
          borderRadius: 18,
          boxShadow: '0 40px 80px rgba(0,0,0,0.5)',
          fontFamily: t.fontUi,
          ...(cardStyle || {}),
        }}
      >
        {header ? <div className="calm-modal-header">{header}</div> : null}

        <div className="calm-modal-body" style={bodyStyle || undefined}>
          {children}
        </div>

        {footer ? <div className="calm-modal-footer">{footer}</div> : null}
      </Card>
    </div>
  );
}
